
import { useState, useEffect } from 'react';
import { useIsMobile } from './use-mobile';
import * as UAParserLib from 'ua-parser-js';

// Define the BeforeInstallPromptEvent since it's not in standard DOM types
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// New interface for dismissal data
interface DismissalData {
  dismissed: boolean;
  timestamp: number;
  count: number;
}

export function usePwaInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Use UA Parser for more reliable device detection
    const parser = new UAParserLib.UAParser();
    const result = parser.getResult();
    
    // Check if the app is already in standalone mode (installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Get dismissal data with counts and timestamp
    const getDismissalData = (): DismissalData => {
      const rawData = localStorage.getItem('pwa-install-dismissed');
      if (!rawData) {
        return { dismissed: false, timestamp: 0, count: 0 };
      }
      
      try {
        return JSON.parse(rawData);
      } catch (e) {
        // For backward compatibility with old format
        return { 
          dismissed: rawData === 'true', 
          timestamp: Date.now(), 
          count: 1 
        };
      }
    };
    
    const dismissalData = getDismissalData();
    const hasDismissed = dismissalData.dismissed;
    
    // Check if a week has passed since dismissal (7 days = 604800000 ms)
    const weekInMs = 7 * 24 * 60 * 60 * 1000;
    const timeSinceDismissal = Date.now() - dismissalData.timestamp;
    const showAgainAfterTime = dismissalData.count < 3 && timeSinceDismissal > weekInMs;
    
    // Log detection parameters for debugging
    console.log('PWA Detection:', {
      osName: result.os.name,
      maxTouchPoints: navigator.maxTouchPoints,
      hasTouch: 'ontouchend' in document,
      userAgent: navigator.userAgent,
      isStandalone,
      dismissalData,
      showAgainAfterTime
    });
    
    // Check if it's an iOS device using UA Parser
    const detectedIsIOS = result.os.name === 'iOS' || 
      // Fallback for iPads that might report as macOS with touch capability
      (result.os.name === 'Mac OS' && 
       navigator.maxTouchPoints > 1 && 
       'ontouchend' in document);
    
    console.log('iOS detection result:', detectedIsIOS);
    setIsIOS(detectedIsIOS);
    
    // Listen for the beforeinstallprompt event (Chrome, Edge, Android)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      
      // Stash the event so it can be triggered later
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      console.log('BeforeInstallPrompt event fired');
    };
    
    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      // Clear the install prompt
      setInstallPrompt(null);
      setIsInstallable(false);
      
      // Log analytics event if needed
      console.log('PWA was installed');
      
      // Update localStorage to prevent showing the banner again
      const finalDismissalData: DismissalData = {
        dismissed: true,
        timestamp: Date.now(),
        count: dismissalData.count + 1
      };
      localStorage.setItem('pwa-install-dismissed', JSON.stringify(finalDismissalData));
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // If the app is already installed, don't show installation prompt
    if (isStandalone) {
      console.log('App is already in standalone mode');
      setIsInstallable(false);
    } else if (hasDismissed && !showAgainAfterTime) {
      console.log('User previously dismissed the install prompt and criteria not met to show again');
      setIsInstallable(false);
    } else if (detectedIsIOS && isMobile) {
      // For iOS devices, we need to show custom install instructions
      // since they don't support beforeinstallprompt
      console.log('Setting iOS device as installable');
      setIsInstallable(true);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isMobile]);
  
  const promptToInstall = async () => {
    if (!installPrompt) {
      console.log('No install prompt available');
      return Promise.reject(new Error('No installation prompt available'));
    }
    
    try {
      // Show the install prompt
      await installPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const choiceResult = await installPrompt.userChoice;
      
      // Reset the install prompt - it can only be used once
      setInstallPrompt(null);
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the PWA install prompt');
        return Promise.resolve('accepted');
      } else {
        console.log('User dismissed the PWA install prompt');
        // Get current dismissal data
        const rawData = localStorage.getItem('pwa-install-dismissed');
        let dismissalData: DismissalData;
        
        if (!rawData) {
          dismissalData = { dismissed: true, timestamp: Date.now(), count: 1 };
        } else {
          try {
            dismissalData = JSON.parse(rawData);
            dismissalData.dismissed = true;
            dismissalData.timestamp = Date.now();
            dismissalData.count += 1;
          } catch (e) {
            // For backward compatibility
            dismissalData = { dismissed: true, timestamp: Date.now(), count: 1 };
          }
        }
        
        localStorage.setItem('pwa-install-dismissed', JSON.stringify(dismissalData));
        return Promise.resolve('dismissed');
      }
    } catch (error) {
      console.error('Installation error:', error);
      return Promise.reject(error);
    }
  };
  
  const resetInstallDismissed = () => {
    localStorage.removeItem('pwa-install-dismissed');
    window.location.reload();
  };
  
  return {
    isInstallable: isInstallable && isMobile,
    isIOS,
    promptToInstall,
    resetInstallDismissed
  };
}
